import { DATE_TIME_COPY } from "config/locationCopy";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { FC } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { ClockIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";
import { start } from "repl";
import ClearDataButton from "../ClearDataButton";

export interface RentalCarTimesRangeInputProps {
  className?: string;
  fieldClassName?: string;
  hasButtonSubmit?: boolean;
  dropOffLocationType?: boolean;
  searchBar?: boolean;
  design?: string;
  showTimeType?: "both" | "start" | "end";
  isStartDateLocked?: boolean;
  isLocked?: boolean;
  previewStartDate?: Date | null;
  previewEndDate?: Date | null;
}

const timeSlots = Array.from({ length: 24 }, (_, h) => [
  `${h.toString().padStart(2, "0")}:00`,
  `${h.toString().padStart(2, "0")}:30`,
]).flat();

const RentalCarTimesRangeInput: FC<RentalCarTimesRangeInputProps> = ({
  className = "",
  fieldClassName = "[ nc-hero-field-padding ]",
  hasButtonSubmit = true,
  dropOffLocationType,
  searchBar,
  design,
  showTimeType = "both",
  isStartDateLocked = false,
  isLocked = false,
  previewStartDate = null,
  previewEndDate = null,
}) => {
  const {
    startDate,
    loadingAvailableFleet,
    ourCompany,
    setStartDate,
    setEndDate,
    endDate,
    pickupLocation,
    dropOffLocation,
    dropoffLocationType,
    callAvailableFleetAPI,
    partiallyUnavailableTimes,
    startTime,
    endTime,
    setStartTime,
    setEndTime,
    activeTab,
    s_Time,
    e_Time,
  } = useData();

  const location = useLocation();
  const navigate = useNavigate();
  const popoverPanelRef = useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const isUpdatingFromTimeChange = useRef(false);

  const isHomePage = location.pathname === "/" || location.pathname === "/home";

  // Effective dropoff location: different drop-off if selected, else pickup
  const effectiveDropoffLocation =
    dropoffLocationType === false && dropOffLocation
      ? dropOffLocation
      : pickupLocation;

  // Normalize any time string to "HH:mm" (strips seconds if API returns "HH:mm:ss")
  const normalizeTime = (t: string): string =>
    moment(t, ["HH:mm:ss", "HH:mm"]).format("HH:mm");

  // Returns true when a time slot is outside the location's operational window
  const isOutsideOperationalHours = (
    time: string,
    opStart: string | null | undefined,
    opEnd: string | null | undefined,
  ): boolean => {
    if (!opStart || !opEnd) return false;
    const t = moment(time, "HH:mm");
    return (
      t.isBefore(moment(normalizeTime(opStart), "HH:mm")) ||
      t.isAfter(moment(normalizeTime(opEnd), "HH:mm"))
    );
  };

  // Clamp a time to [opStart, opEnd]. Returns the time unchanged if no operating hours are set
  // or the time is already within the window.
  const clampToOperationalHours = (
    time: string,
    opStart: string | null | undefined,
    opEnd: string | null | undefined,
    fallback: "start" | "end",
  ): string => {
    if (!opStart || !opEnd) return time;
    const normStart = normalizeTime(opStart);
    const normEnd = normalizeTime(opEnd);
    const t = moment(time, "HH:mm");
    if (t.isBefore(moment(normStart, "HH:mm")) || t.isAfter(moment(normEnd, "HH:mm"))) {
      return fallback === "start" ? normStart : normEnd;
    }
    return time;
  };

  // Snap to operational hours only when the time is still the app default (user hasn't changed it).
  // If the user has picked a custom time, only clamp it if it falls outside the operating window.
  const snapOrClampStart = (prev: string, opStart: string, opEnd: string): string => {
    const normStart = normalizeTime(opStart);
    const normEnd = normalizeTime(opEnd);
    if (prev === s_Time) return normStart;   // default → snap to opening time
    return clampToOperationalHours(prev, normStart, normEnd, "start");
  };

  const snapOrClampEnd = (prev: string, opStart: string, opEnd: string): string => {
    const normStart = normalizeTime(opStart);
    const normEnd = normalizeTime(opEnd);
    if (prev === e_Time) return normEnd;     // default → snap to closing time
    return clampToOperationalHours(prev, normStart, normEnd, "end");
  };

  useEffect(() => {
    const opStart = pickupLocation?.operational_start_time;
    const opEnd = pickupLocation?.operational_end_time;

    if (opStart && opEnd && showTimeType !== "end") {
      setStartTime((prev) => snapOrClampStart(prev, opStart, opEnd));
    }
    if (dropoffLocationType !== false && opStart && opEnd && showTimeType !== "start") {
      setEndTime((prev) => snapOrClampEnd(prev, opStart, opEnd));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupLocation?.id]);

  // Clamp/snap endTime when dropoff location or mode changes
  useEffect(() => {
    if (dropoffLocationType === false && dropOffLocation?.operational_start_time && dropOffLocation?.operational_end_time && showTimeType !== "start") {
      setEndTime((prev) => snapOrClampEnd(prev, dropOffLocation.operational_start_time!, dropOffLocation.operational_end_time!));
    } else if (dropoffLocationType !== false && pickupLocation?.operational_start_time && pickupLocation?.operational_end_time && showTimeType !== "start") {
      setEndTime((prev) => snapOrClampEnd(prev, pickupLocation.operational_start_time!, pickupLocation.operational_end_time!));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropOffLocation?.id, dropoffLocationType]);

  // Do not block times when on the home page
  const shouldBlockTime = !isHomePage && (
    location.pathname === "/listing-car-detail" ||
    location.pathname === "/car-listing-detail" ||
    Object.keys(partiallyUnavailableTimes).length > 0
  );

  useEffect(() => {
    validateEndTime();
  }, [startDate]);

  const validateEndTime = () => {
    if (
      startDate &&
      endDate &&
      startDate.toDateString() === endDate.toDateString()
    ) {
      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const startTimeObj = new Date();
      startTimeObj.setHours(startHour, startMinute);

      const endTimeObj = new Date();
      endTimeObj.setHours(endHour, endMinute);
    }
  };

  const formatTime = (time: string, type: "startTime" | "endTime") => {
    const use24Hour = ourCompany?.default_time_format === "24_hour";

    if (!time) return type === "startTime" ? "Start time" : "End time"; // Fallback for empty/null

    if (time === "00:00") {
      return use24Hour ? "00:00" : "Midnight";
    }

    const m = moment(time, "HH:mm");
    if (!m.isValid()) return type === "startTime" ? "Start time" : "End time";

    return use24Hour ? m.format("HH:mm") : m.format("h:mm A");
  };

  const renderInput = () => {
    return (
      <>
        {showTimeType === "both" && (
          <div className="text-text-color ">
            <ClockIcon className="w-5 h-5 lg:w-7 lg:h-7" />
          </div>
        )}
        <div className="flex-grow text-left">
          <span
            className={`block xl:text-lg ${showTimeType === "both" ? "font-semibold" : ""} ${
              searchBar === false ? "text-text-color" : "text-text-color"
            }`}
          >
            {showTimeType === "start" &&
              `${formatTime(startTime, "startTime")}`}
            {showTimeType === "end" && `${formatTime(endTime, "endTime")}`}
            {showTimeType === "both" &&
              `${formatTime(startTime, "startTime")} - ${formatTime(endTime, "endTime")}`}
          </span>
          <span
            className={`block mt-1 text-sm  ${
              searchBar === false ? "text-text-color" : "text-text-color"
            } leading-none font-light`}
          >
            {showTimeType === "both" && DATE_TIME_COPY.timeRange}
          </span>
        </div>
        {showTimeType === "start" && startTime && startTime !== "07:00" && (
          <ClearDataButton onClick={() => setStartTime("07:00")} />
        )}
        {showTimeType === "end" && endTime && endTime !== "19:00" && (
          <ClearDataButton onClick={() => setEndTime("19:00")} />
        )}
      </>
    );
  };

  useEffect(() => {
    // Skip for weekly/monthly tabs - dateRangeSelection handles date calculation for those
    if (activeTab === 'weeks' || activeTab === 'months') {
      return;
    }
    
    if (startDate && endDate) {
      isUpdatingFromTimeChange.current = true;
      
      const startDateTime = moment(
        `${moment(startDate).format("YYYY-MM-DD")} ${startTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();

      const endDateTime = moment(
        `${moment(endDate).format("YYYY-MM-DD")} ${endTime}`,
        "YYYY-MM-DD HH:mm",
      ).toDate();

      setStartDate(startDateTime);
      setEndDate(endDateTime);
      setTimeout(() => {
        isUpdatingFromTimeChange.current = false;
      }, 100);
    }
  }, [startTime, endTime, activeTab]);

  // Define time range type
  type TimeRange = {
    from: string; // HH:mm:ss
    to: string; // HH:mm:ss
  };

  type PartialUnavailability = {
    [date: string]: TimeRange[];
  };

  const getPartialUnavailableRanges = (
    date: Date | null | undefined,
  ): TimeRange[] => {
    if (!date) return [];
    const m = moment(date);
    if (!m.isValid()) return [];
    const dateKey = m.format("YYYY-MM-DD");
    const ranges = partiallyUnavailableTimes[dateKey];
    return Array.isArray(ranges) ? ranges : [];
  };

  const isTimeBlocked = (
    date: Date | null | undefined,
    time: string,
  ): boolean => {
    if (!date) return false;
    const ranges = getPartialUnavailableRanges(date);
    if (ranges.length === 0) return false;
    
    const timeMoment = moment(time, "HH:mm");
    // Check if time is WITHIN any unavailable range
    return ranges.some(({ from, to }) => {
      const fromMoment = moment(from, "HH:mm:ss");
      const toMoment = moment(to, "HH:mm:ss");
      return (
        timeMoment.isSameOrAfter(fromMoment) &&
        timeMoment.isSameOrBefore(toMoment)
      );
    });
  };

  // Find the next available time from a given slot in your 30-min list.
  // Returns null when every slot within operational hours is also booking-blocked.
  const getNextAvailableTime = (
    date: Date | null | undefined,
    time: string,
    slots: string[],
    opStart?: string | null,
    opEnd?: string | null,
  ): string | null => {
    if (!date) return time;

    const isOpBlocked = (t: string) =>
      isOutsideOperationalHours(t, opStart, opEnd);

    // Forward scan from current slot
    for (let i = slots.indexOf(time); i < slots.length; i++) {
      if (!isTimeBlocked(date, slots[i]) && !isOpBlocked(slots[i])) return slots[i];
    }

    // Wrap-around scan from beginning
    for (let i = 0; i < slots.length; i++) {
      if (!isTimeBlocked(date, slots[i]) && !isOpBlocked(slots[i])) return slots[i];
    }

    // No valid slot found
    return null;
  };

  useEffect(() => {
    // Skip blocked time check if the date change was caused by a time change
    if (isUpdatingFromTimeChange.current) {
      return;
    }
    if (!shouldBlockTime) {
      return;
    }
    
    // Use preview dates if provided, otherwise use actual dates
    const checkStartDate = previewStartDate || startDate;
    const checkEndDate = previewEndDate || endDate;

    if (checkStartDate && startTime && isTimeBlocked(checkStartDate, startTime)) {
      const next = getNextAvailableTime(
        checkStartDate,
        startTime,
        timeSlots,
        pickupLocation?.operational_start_time,
        pickupLocation?.operational_end_time,
      );
      if (next === null) {
        const opStart = pickupLocation?.operational_start_time;
        const opEnd = pickupLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStart, "startTime")} – ${formatTime(opEnd, "endTime")}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
      } else {
        setStartTime(next);
      }
    }
    if (checkEndDate && endTime && isTimeBlocked(checkEndDate, endTime)) {
      const next = getNextAvailableTime(
        checkEndDate,
        endTime,
        timeSlots,
        effectiveDropoffLocation?.operational_start_time,
        effectiveDropoffLocation?.operational_end_time,
      );
      if (next === null) {
        const opStart = effectiveDropoffLocation?.operational_start_time;
        const opEnd = effectiveDropoffLocation?.operational_end_time;
        toast.error(
          opStart && opEnd
            ? `No available time slot on the selected date. Operational hours: ${formatTime(opStart, "startTime")} – ${formatTime(opEnd, "endTime")}.`
            : "No available time slot on the selected date."
        );
        setStartDate(null);
        setEndDate(null);
      } else {
        setEndTime(next);
      }
    }
  }, [startDate, endDate, previewStartDate, previewEndDate, shouldBlockTime]);

  useEffect(() => {
    if (isPopoverOpen && window.innerWidth < 768) {
      setTimeout(() => {
        if (popoverPanelRef.current) {
          const topPosition =
            popoverPanelRef.current.getBoundingClientRect().top;
          const offset =
            window.pageYOffset || document.documentElement.scrollTop;
          const targetScrollPosition = topPosition + offset - 120; // Add padding at the top

          window.scrollTo({
            top: targetScrollPosition,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [isPopoverOpen]);

  return (
    <>
      <Popover
        className={`RentalCarDatesRangeInput relative flex ${className}`}
      >
        {({ open }) => {
          if (open !== isPopoverOpen) {
            setIsPopoverOpen(open);
          }
          if (window.innerWidth < 768 && open) {
            setTimeout(() => {
              const panel = popoverPanelRef.current;
              if (panel) {
                const rect = panel.getBoundingClientRect();
                const isVisible =
                  rect.top >= 0 && rect.bottom <= window.innerHeight;

                if (!isVisible) {
                  window.scrollTo({
                    top: window.pageYOffset + rect.top - 120,
                    behavior: "smooth",
                  });
                }
              }
            }, 150);
          }
          return (
            <>
              <div
                className={`flex-1 z-10  flex items-center focus:outline-none ${
                  open
                    ? `${showTimeType === "both" ? "nc-hero-field-focused !rounded-md" : ""}`
                    : ""
                } ${(showTimeType === "start" && (isStartDateLocked || isLocked)) ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <PopoverButton
                  disabled={showTimeType === "start" && (isStartDateLocked || isLocked)}
                  className={`flex-1 z-10 flex relative ${fieldClassName} items-center space-x-3 focus:outline-none ${(showTimeType === "start" && (isStartDateLocked || isLocked)) ? "cursor-not-allowed" : ""}`}
                  onClickCapture={() => document.querySelector("html")?.click()}
                >
                  {renderInput()}
                </PopoverButton>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <PopoverPanel
                  ref={popoverPanelRef}
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-bg-color rounded-xl shadow-xl border border-secondary-100 overflow-hidden ${showTimeType === "both" ? "w-[340px]" : "w-48"}`}
                >
                  <div className="p-6 overflow-hidden rounded-md shadow-lg ring-1 ring-black ring-opacity-5 bg-secondary-50">
                    <div
                      className={`grid ${showTimeType === "both" ? "grid-cols-2 gap-6" : "grid-cols-1"}`}
                    >
                      {(showTimeType === "start" ||
                        showTimeType === "both") && (
                        <div>
                          <h3 className="text-base font-semibold mb-3 text-center text-text-color">
                            Start Time
                          </h3>

                          <div className="overflow-y-auto h-56 pr-2">
                            {timeSlots.map((time) => {
                              const isUnavailable = shouldBlockTime
                                ? isTimeBlocked((previewStartDate || startDate) ?? null, time)
                                : false;
                              const isOperationallyBlocked = isOutsideOperationalHours(
                                time,
                                pickupLocation?.operational_start_time,
                                pickupLocation?.operational_end_time,
                              );

                              return (
                                <div
                                  key={time}
                                  className={`cursor-pointer text-center py-2 px-3 mb-1 rounded-lg transition text-sm ${
                                    startTime === time
                                      ? "bg-primary-200 text-on-primary font-medium"
                                      : "text-text-color hover:bg-secondary-100"
                                  } ${
                                    isUnavailable || isOperationallyBlocked
                                      ? "opacity-50 cursor-not-allowed line-through"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (isUnavailable) {
                                      toast.error(
                                        "This time slot is unavailable due to a partial booking",
                                      );
                                      return;
                                    }
                                    if (isOperationallyBlocked) {
                                      toast.error(
                                        "This time is outside the location's operational hours",
                                      );
                                      return;
                                    }
                                    setStartTime(time);
                                  }}
                                >
                                  {formatTime(time, "startTime")}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {(showTimeType === "end" || showTimeType === "both") && (
                        <div>
                          <h3 className="text-base font-semibold mb-3 text-center text-text-color">
                            End Time
                          </h3>
                          <div className="overflow-y-auto h-56 pr-2">
                            {timeSlots.map((time) => {
                              const isUnavailable = shouldBlockTime
                                ? isTimeBlocked((previewEndDate || endDate) ?? null, time)
                                : false;
                              const isOperationallyBlocked = isOutsideOperationalHours(
                                time,
                                effectiveDropoffLocation?.operational_start_time,
                                effectiveDropoffLocation?.operational_end_time,
                              );

                              return (
                                <div
                                  key={time}
                                  className={`cursor-pointer text-center py-2 px-3 mb-1 rounded-lg transition text-sm ${
                                    endTime === time
                                      ? "bg-primary-200 text-on-primary font-medium"
                                      : "text-text-color hover:bg-secondary-100"
                                  } ${
                                    isUnavailable || isOperationallyBlocked
                                      ? "opacity-50 cursor-not-allowed line-through"
                                      : ""
                                  }`}
                                  onClick={() => {
                                    if (isUnavailable) {
                                      toast.error(
                                        "This time slot is unavailable due to a partial booking",
                                      );
                                      return;
                                    }
                                    if (isOperationallyBlocked) {
                                      toast.error(
                                        "This time is outside the location's operational hours",
                                      );
                                      return;
                                    }
                                    setEndTime(time);
                                  }}
                                >
                                  {formatTime(time, "endTime")}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <PopoverButton className="mt-5 w-full bg-primary-100 hover:bg-primary-200 text-on-primary font-semibold py-3 rounded-lg transition">
                      Save
                    </PopoverButton>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          );
        }}
      </Popover>
      {location.pathname !== "/car-listing-detail" &&
        hasButtonSubmit &&
        location.pathname !== "/listing-car-detail" &&
        design === "designA" && (
          <div className="cursor-pointer min-w-30 mb-0 self-center">
            {hasButtonSubmit && (
              <div className="flex justify-end md:pr-4">
                <div
                  onClick={() => {
                    if (pickupLocation === null) {
                      toast.error("Please enter Pick up location");
                    } else if (
                      dropOffLocation === null &&
                      dropOffLocationType === false
                    ) {
                      toast.error("Please enter Drop off location");
                    } else if (pickupLocation !== null) {
                      callAvailableFleetAPI(navigate);
                    }
                  }}
                  className={`h-14 md:h-16 w-full lg:w-16 rounded-md bg-primary-200 hover:bg-primary-700 lg:px-5 flex items-center justify-center text-text-color focus:outline-none ${
                    startDate && endDate ? "" : ""
                  }`}
                >
                  {loadingAvailableFleet ? (
                    <svg
                      aria-hidden="true"
                      className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-400"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  ) : (
                    <>
                      <span className="flex lg:hidden text-lg font-bold text-on-primary">
                        Search
                      </span>
                      <span className="hidden lg:flex">
                        <div className="text-on-primary">
                          <MagnifyingGlassIcon className="w-6 h-6" />
                        </div>
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
    </>
  );
};

export default RentalCarTimesRangeInput;
